/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Slices
import {
	createMainSlice,
	IMainSlice,
} from '@features/main/slices/createMainSlice';

import {
	createProductionsSlice,
	IProductionSlice,
} from '@features/sourcing/productions/slices/createProductionsSlice';

import { createProductionStartMintSlice } from '@features/sourcing/productions/components/StartMint/slices/createProductionsStartMintSlice';

import {
	createMintHistorySlice,
	IMintHistorySlice,
} from '@features/sourcing/mint-history/slices/createMintHistorySlice';

import {
	createApiTokensSlice,
	IApiTokenSlice,
} from '@features/resources/api-tokens/slices/createApiTokensSliceV2';

import {
	createAuthSlice,
	IAuthSlice,
} from '@features/auth/slices/createAuthSlice';

import {
	IConversionFactorSlice,
	createConversionFactorsSlice,
} from '@features/resources/conversion-factors/slice/createConversionFactorSlice';

import { IProductionStartMintSlice } from '@features/sourcing/productions/components/StartMint/slices/createProductionsStartMintSlice';

import {
	createMeasurabilityProjectsSlice,
	IMeasurabilityProjectSlice,
} from '@features/catalogs/meausrability-projects/list/slice/createMeasurabilityProjectSlice';

import {
	createAdditionalityProjectsSlice,
	IAdditionalityProjectSlice,
} from '@features/catalogs/additionality-projects/list/slice/createAdditionalityProjectSlice';

import {
	createMeterPointsSlice,
	IMeterPointSlice,
} from '@features/resources/meter-points/slice/createMeterPointSlice';

import {
	createSitesSlice,
	ISiteSlice,
} from '@features/resources/sites/slice/createSitesSlice';

import {
	createOffsetsDatatableSlice,
	IOffsetsDatatableSlice,
} from '@features/offsets/slices/createOffsetsDatatableSlice';

import {
	createImportHistorySlice,
	IImportHistorySlice,
} from '@features/sourcing/import-history/slices/createImportHistorySlice';

import {
	createProductionsDatatableSlice,
	IProductionsDatatableSlice,
} from '@features/sourcing/productions/slices/createProductionsDatatableSlice';

import {
	createApiTokensDatatableSlice,
	IApiTokensDatatableSlice,
} from '@features/resources/api-tokens/slices/createApiTokensDatatableSlice';

import {
	createSitesDatatableSlice,
	ISitesDatatableSlice,
} from '@features/resources/sites/slice/createSitesDatatableSlice';

import {
	createMeterpointsDatatableSlice,
	IMeterpointsDatatableSlice,
} from '@features/resources/meter-points/slice/createMeterPointDatatableSlice';

import {
	createConversionFactorsDatatableSlice,
	IConversionFactorsDatatableSlice,
} from '@features/resources/conversion-factors/slice/createConversionFactorDatatableSlice';

import {
	createMeasurabilitiesSlice,
	IMeasurabilitiesSlice,
} from '@features/resources/measurabilities/slices/createMeasurabilitieeSlice';

import {
	createMeasurabilitiesDatatableSlice,
	IMeasurabilitiesDatatableSlice,
} from '@features/resources/measurabilities/slices/createMeasurabilitiesDatatableSlice';

import {
	createAdditionalitiesDatatableSlice,
	IAdditionalitiesDatatableSlice,
} from '@features/resources/additionalities/slices/createAdditionalitiesDatatableSlice';

import {
	createAdditionalitiesSlice,
	IAdditionalitiesSlice,
} from '@features/resources/additionalities/slices/createAdditionalitiesSlice';

import {
	IOffsetSlice,
	createOffsetSlice,
} from '@features/offsets/slices/createOffsetsSlice';

import {
	IProducerSlice,
	createProducersSlice,
} from '@features/resources/producers/slices/createProducersSlice';

import {
	IProducersDatatableSlice,
	createProducersDatatableSlice,
} from '@features/resources/producers/slices/createProducersDatatableSlice';

import {
	IBrandSlice,
	createBrandsSlice,
} from '@features/resources/brands/slices/createBrandsSlice';

import {
	IBrandsDatatableSlice,
	createBrandsDatatableSlice,
} from '@features/resources/brands/slices/createBrandsDatatableSlice';

import {
	IOrganizationSlice,
	createOrganizationsSlice,
} from '@features/resources/organizations/slices/createOrganizationsSlice';

import {
	IOrganizationsDatatableSlice,
	createOrganizationsDatatableSlice,
} from '@features/resources/organizations/slices/createOrganizationsDatatableSlice';

import {
	IUserSlice,
	createUsersSlice,
} from '@features/resources/users/slices/createUsersSlice';

import {
	IUsersDatatableSlice,
	createUsersDatatableSlice,
} from '@features/resources/users/slices/createUsersDatatableSlice';

interface MainStore {}

export type TStore = MainStore &
	IConversionFactorSlice &
	IProductionStartMintSlice &
	IMeasurabilityProjectSlice &
	IAdditionalityProjectSlice &
	IProductionSlice &
	ISiteSlice &
	IMeterPointSlice &
	IImportHistorySlice &
	IOffsetsDatatableSlice &
	IProductionsDatatableSlice &
	IApiTokensDatatableSlice &
	ISitesDatatableSlice &
	IApiTokenSlice &
	IMeterpointsDatatableSlice &
	IConversionFactorsDatatableSlice &
	IMeasurabilitiesSlice &
	IMeasurabilitiesDatatableSlice &
	IAdditionalitiesSlice &
	IAdditionalitiesDatatableSlice &
	IMintHistorySlice &
	IMainSlice &
	IAuthSlice &
	IOffsetSlice &
	IProducerSlice &
	IProducersDatatableSlice &
	IBrandSlice &
	IBrandsDatatableSlice &
	IOrganizationSlice &
	IOrganizationsDatatableSlice &
	IUserSlice &
	IUsersDatatableSlice;

/* -------------------------------------------------------------------------- */
/*                                Global state                                */
/* -------------------------------------------------------------------------- */
export const useStore = create(
	persist(
		(set, get: any, storeApi: any) => ({
			...createOffsetSlice(set, get, storeApi),
			...createAuthSlice(set, get, storeApi),
			...createMainSlice(set, get, storeApi),
			...createMintHistorySlice(set, get, storeApi),
			...createProductionStartMintSlice(
				set,
				get,
				storeApi
			),
			...createSitesSlice(set, get, storeApi),
			...createProductionsSlice(set, get, storeApi),
			...createApiTokensSlice(set, get, storeApi),
			...createConversionFactorsSlice(set, get, storeApi),
			...createMeasurabilityProjectsSlice(
				set,
				get,
				storeApi
			),
			...createAdditionalityProjectsSlice(
				set,
				get,
				storeApi
			),
			...createMeterPointsSlice(set, get, storeApi),
			...createImportHistorySlice(set, get, storeApi),
			...createOffsetsDatatableSlice(set, get, storeApi),
			...createProductionsDatatableSlice(
				set,
				get,
				storeApi
			),
			...createApiTokensDatatableSlice(
				set,
				get,
				storeApi
			),
			...createSitesDatatableSlice(set, get, storeApi),
			...createMeasurabilitiesSlice(set, get, storeApi),
			...createAdditionalitiesSlice(set, get, storeApi),
			...createMeterpointsDatatableSlice(
				set,
				get,
				storeApi
			),
			...createConversionFactorsDatatableSlice(
				set,
				get,
				storeApi
			),
			...createMeasurabilitiesDatatableSlice(
				set,
				get,
				storeApi
			),
			...createAdditionalitiesDatatableSlice(
				set,
				get,
				storeApi
			),
			...createProducersDatatableSlice(
				set,
				get,
				storeApi
			),
			...createProducersSlice(set, get, storeApi),
			...createBrandsSlice(set, get, storeApi),
			...createBrandsDatatableSlice(set, get, storeApi),
			...createOrganizationsSlice(set, get, storeApi),
			...createOrganizationsDatatableSlice(
				set,
				get,
				storeApi
			),
			...createUsersSlice(set, get, storeApi),
			...createUsersDatatableSlice(set, get, storeApi),
		}),
		{
			name: 'IGLU-store',
			partialize: (s: any) => ({}),
		}
	)
);

export default useStore;
